import { useEffect, useState } from "react";
import { BookOpen, ChevronRight, Sigma } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

export default function ConceptPicker({ open, onOpenChange, board, standard, onSelect }) {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeChapter, setActiveChapter] = useState(null);

  useEffect(() => {
    if (!open || !board || !standard) return;
    setLoading(true);
    api.get(`/curriculum/tree`, { params: { board, standard } })
      .then(({ data }) => setTree(data))
      .finally(() => setLoading(false));
  }, [open, board, standard]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="concept-picker-modal" className="max-w-lg w-[calc(100%-2rem)] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Pick a topic</DialogTitle>
          <DialogDescription>Choose a chapter and concept to continue.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto scrollbar-thin -mx-1 px-1">
          {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading curriculum...</p>}
          {!loading && tree.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">No topics available yet for Class {standard} {board}.</p>
          )}
          {!loading && tree.map((subject) => (
            <div key={subject.id} className="mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-primary mb-1.5 flex items-center gap-1.5">
                <Sigma size={13} /> {subject.name}
              </p>
              {subject.chapters.map((chapter) => (
                <div key={chapter.id} className="mb-1">
                  <button
                    data-testid={`chapter-toggle-${chapter.id}`}
                    onClick={() => setActiveChapter(activeChapter === chapter.id ? null : chapter.id)}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-muted text-sm font-semibold"
                  >
                    <span className="flex items-center gap-2"><BookOpen size={15} className="text-muted-foreground" />{chapter.name}</span>
                    <ChevronRight size={16} className={`transition-transform ${activeChapter === chapter.id ? "rotate-90" : ""}`} />
                  </button>
                  {activeChapter === chapter.id && (
                    <div className="pl-8 py-1 space-y-1">
                      {chapter.concepts.map((concept) => (
                        <button
                          key={concept.id}
                          data-testid={`concept-option-${concept.id}`}
                          onClick={() => onSelect(concept.id, concept.name)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          {concept.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
